import React, { useCallback, useRef, useState } from "react";
import { connect } from "react-redux";

import Form, { FormInstance } from "antd/es/form";
import Row from "antd/es/row";
import { FormChangeInfo, FormFinishInfo } from "rc-field-form/lib/FormContext";
import { FormattedMessage, useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { Button, Modal } from "@mapmycustomers/ui";

import { getFunnels } from "@app/store/deal";
import { createFunnel, updateFunnel, updateStages } from "@app/store/deal/actions";
import { RootState } from "@app/store/rootReducer";

import DealFunnel from "./DealFunnel";
import styles from "./DealFunnelModal.module.scss";

interface Props {
  defaultFunnelId?: Funnel["id"];
  funnels: Funnel[];
  onCreateFunnel: typeof createFunnel.request;
  onHide: () => void;
  onUpdateFunnel: typeof updateFunnel.request;
  onUpdateStages: typeof updateStages.request;
}

const ManageFunnelsModal: React.FC<Props> = ({
  defaultFunnelId,
  funnels,
  onCreateFunnel,
  onHide,
  onUpdateFunnel,
  onUpdateStages,
}) => {
  const intl = useIntl();
  const [highlightedFunnelId, setHighlightedFunnelId] = useState<Funnel["id"] | undefined>(
    defaultFunnelId
  );

  const handleAddFunnel = useCallback(() => {
    const funnel = { name: "" };
    onCreateFunnel({
      callback: (addedFunnel: Funnel) => {
        setHighlightedFunnelId(addedFunnel.id);
      },
      funnel,
    });
  }, [onCreateFunnel, setHighlightedFunnelId]);

  const changedForms = useRef<Map<string, FormInstance>>(new Map());
  const handleFormChange = useCallback((name: string, info: FormChangeInfo) => {
    Object.keys(info.forms).forEach((key) => {
      if (info.forms[key].isFieldsTouched(false)) {
        changedForms.current.set(key, info.forms[key] as FormInstance);
      }
    });
  }, []);

  const handleSave = useCallback(() => {
    for (const form of changedForms.current.values()) {
      form.submit();
    }
  }, []);

  const handleFormFinish = useCallback(
    (name: string, info: FormFinishInfo) => {
      const [formType, id] = name.split("_");
      const funnelId = parseInt(id, 10) as Funnel["id"];
      if (formType === "funnel") {
        const originalFunnel = funnels.find(({ id }) => id === funnelId);
        if (originalFunnel) {
          const updatedFunnel = { ...originalFunnel, name: info.values.name };
          onUpdateFunnel({ funnel: updatedFunnel });
          changedForms.current.delete(name);
        }
      } else if (formType === "stages") {
        onUpdateStages({
          funnelId: funnelId,
          stages: info.values.stages.map((stage: Stage) => ({
            ...stage,
            weight: stage.weight >= 1 ? stage.weight / 100 : stage.weight,
          })),
        });
        changedForms.current.delete(name);
      }

      // if all forms saved, we can close the modal
      if (!changedForms.current.size) {
        onHide();
      }
    },
    [funnels, onUpdateFunnel, onUpdateStages, onHide]
  );

  return (
    <Modal
      className={styles.funnelModalContainer}
      okText={intl.formatMessage({
        id: "deals.setting.funnelModal.ok",
        defaultMessage: "Save",
        description: "Title for Save button in Deal Funnel modal",
      })}
      onCancel={onHide}
      onOk={handleSave}
      open
      title={
        <span className={styles.modalTitle}>
          {intl.formatMessage({
            id: "deals.setting.funnelModal.title",
            defaultMessage: "Deal Funnels",
            description: "Title for Deal Funnel modal",
          })}
        </span>
      }
      width="750px"
    >
      <section className={styles.content}>
        <section>
          <Row className={styles.subtitleRow} justify="space-between">
            <FormattedMessage
              id="deals.settings.funnelModal.subtitle"
              defaultMessage="Add, delete, and modify your deal funnels and stages"
              description="Subtitle for Deal Funnel Modal"
              tagName="p"
            />
            <Button onClick={handleAddFunnel} type="text">
              {intl.formatMessage({
                id: "deals.setting.funnelModal.addFunnel",
                defaultMessage: "+ Add a New Funnel",
                description: "Add Funnel Button on funnel Setting Modal",
              })}
            </Button>
          </Row>
        </section>
        <Form.Provider onFormChange={handleFormChange} onFormFinish={handleFormFinish}>
          {funnels.map((funnel) => (
            <DealFunnel
              funnel={funnel}
              highlighted={highlightedFunnelId === funnel.id}
              key={funnel.id}
              onHide={onHide}
            />
          ))}
        </Form.Provider>
      </section>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
});

const mapDispatchToProps = {
  onCreateFunnel: createFunnel.request,
  onUpdateFunnel: updateFunnel.request,
  onUpdateStages: updateStages.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageFunnelsModal);
